import { FC, useMemo } from 'react';
import cx from 'classnames';
import parse from 'html-react-parser';
import { MonitoringAddressItem } from '@/redux/models';
import {
  getFormattedDateWithTime,
  getIconByNetwork,
  getShortAddress,
} from '@/helpers';
import {
  ChainsDictionary,
  MonitoringStatusEnum,
  MonitoringStatusNames,
} from '@/config';
import {
  BlacklistBadge,
  Button,
  CopyText,
  RiskBadge,
  TableFigure,
} from '@/components/index';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';

import styles from './HistoryAddressItem.module.sass';

interface HistoryAddressItemProps {
  item: MonitoringAddressItem;
  isShowDemoData?: boolean;
  onUnsubscribe: (id: number) => void;
  loading: boolean;
}

const HistoryAddressItem: FC<HistoryAddressItemProps> = (props) => {
  const { item, onUnsubscribe, loading, isShowDemoData } = props;
  const { t } = useTranslation();

  const networkIcon = getIconByNetwork(item.asset);
  const shortAddress = getShortAddress(item.address);
  const nextCheck = getFormattedDateWithTime(
    item.next_check_time,
    'DD.MM.YYYY HH:mm',
    true,
  );
  const hasRiskScore = item.riskscore !== null;
  const riskValue = item?.riskscore || 0;
  const blacklistFlag = item.has_blacklist_flag;
  const networkName = ChainsDictionary[item.asset]?.name;

  const unsubscribeHandler = () => onUnsubscribe(item.id);
  const isActive = useMemo(() => {
    return item.status === MonitoringStatusEnum.ACTIVE;
  }, [item.status]);

  const rowClasses = cx(styles.entry, {
    [styles.row]: true,
  });

  return (
    <div className={rowClasses}>
      <div className={`${styles.col} ${styles['col--icon']}`}>
        <img src={networkIcon} alt={item.asset} />
      </div>
      <div className={`${styles.col} ${styles['col--name']}`}>
        <div className={styles.crypto}>
          <div className={styles.crypto__address}>
            <CopyText text={shortAddress} copyText={item.address} />
          </div>
          <div className={styles.crypto__info}>
            <span className={styles.crypto__network}>{networkName}</span>
          </div>
        </div>
      </div>
      <div className={`${styles.col} ${styles['col--risky']}`}>
        {hasRiskScore && !blacklistFlag && <RiskBadge value={riskValue} />}
      </div>
      <div className={`${styles.col} ${styles['col--label']}`}>
        {blacklistFlag ? <BlacklistBadge flag={blacklistFlag} /> : 'n/a'}
      </div>
      <div className={`${styles.col} ${styles['col--next-check']}`}>
        {nextCheck}
      </div>
      <div className={`${styles.col} ${styles['col--status']}`}>
        {t(
          `translation.monitoring_status_${MonitoringStatusNames[item.status]}`,
        )}
      </div>

      <div className={`${styles.col} ${styles['col--actions']}`}>
        <div className={styles.col}>
          {isActive && (
            <Popover
              content={parse(
                t('translation.monitoring_disable_btn_description'),
              )}
            >
              <div>
                <Button
                  size="m"
                  type="secondary"
                  onClick={unsubscribeHandler}
                  disabled={loading || isShowDemoData}
                  nowrap
                >
                  {t('translation.monitoring_disable_btn')}
                </Button>
              </div>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryAddressItem;
